/**
 * Google Analytics service for tracking events and page views
 *
 * This service provides a centralized way to interact with Google Analytics
 * while respecting environment settings and user privacy.
 */

import { env, analyticsConfig } from "../config/env";
import { logger } from "./logger";

// Extend the Window interface to include gtag
declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    gtag: (...args: any[]) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dataLayer: any[];
  }
}

/**
 * Google Analytics service class
 */
export class AnalyticsService {
  private isInitialized = false;
  private readonly measurementId: string;

  constructor(measurementId: string = env.GA_MEASUREMENT_ID) {
    this.measurementId = measurementId;
  }

  /**
   * Initialize Google Analytics with the configured measurement ID
   * This should be called once when the app starts
   */
  initialize(): void {
    if (this.isInitialized) {
      logger.warn("Analytics already initialized");
      return;
    }

    if (!this.measurementId) {
      logger.warn("Google Analytics measurement ID not configured");
      return;
    }

    // Only initialize in production or when explicitly enabled
    if (!analyticsConfig.enabled) {
      logger.log("Analytics disabled in current environment");
      return;
    }

    try {
      // Ensure gtag is available
      if (typeof window !== "undefined" && window.gtag) {
        // Configure the measurement ID
        window.gtag("config", this.measurementId, {
          // Respect user privacy
          anonymize_ip: analyticsConfig.anonymizeIp,
          // Disable advertising features in development
          allow_ad_personalization_signals:
            analyticsConfig.allowAdPersonalization,
          // Custom configuration
          send_page_view: false, // We'll handle page views manually
          debug_mode: analyticsConfig.debugMode,
        });

        this.isInitialized = true;
        logger.log(
          `Google Analytics initialized with ID: ${this.measurementId}`,
        );
      } else {
        logger.warn("Google Analytics (gtag) not available on window object");
      }
    } catch (error) {
      logger.error("Failed to initialize Google Analytics:", error);
    }
  }

  /**
   * Track a page view
   */
  trackPageView(pagePath: string, pageTitle?: string): void {
    if (!this.isInitialized || typeof window === "undefined" || !window.gtag) {
      return;
    }

    try {
      window.gtag("event", "page_view", {
        page_path: pagePath,
        page_title: pageTitle,
      });
      logger.debug("Analytics Page View", { pagePath, pageTitle });
    } catch (error) {
      logger.error("Failed to track page view:", error);
    }
  }

  /**
   * Track a custom event
   */
  trackEvent(
    eventName: string,
    parameters: {
      event_category?: string;
      event_label?: string;
      value?: number;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      [key: string]: any;
    } = {},
  ): void {
    if (!this.isInitialized || typeof window === "undefined" || !window.gtag) {
      return;
    }

    try {
      window.gtag("event", eventName, parameters);
      logger.debug("Analytics Event", { eventName, parameters });
    } catch (error) {
      logger.error("Failed to track event:", error);
    }
  }

  /**
   * Track agent-specific events
   */
  trackAgentEvent(action: string, agentId?: string, agentName?: string): void {
    this.trackEvent(action, {
      event_category: "agent",
      event_label: agentName || agentId,
      agent_id: agentId,
      agent_name: agentName,
    });
  }

  /**
   * Track search events
   */
  trackSearch(searchTerm: string, resultsCount?: number): void {
    this.trackEvent("search", {
      event_category: "search",
      search_term: searchTerm,
      results_count: resultsCount,
    });
  }

  /**
   * Track API errors for monitoring
   */
  trackError(
    errorType: string,
    errorMessage?: string,
    errorCode?: string,
  ): void {
    this.trackEvent("exception", {
      event_category: "error",
      description: `${errorType}: ${errorMessage || "Unknown error"}`,
      error_type: errorType,
      error_code: errorCode,
      fatal: false,
    });
  }

  /**
   * Get the current initialization status
   */
  get initialized(): boolean {
    return this.isInitialized;
  }
}

// Create and export a singleton instance
export const analytics = new AnalyticsService();

// Export convenience functions
export const trackPageView = (pagePath: string, pageTitle?: string) =>
  analytics.trackPageView(pagePath, pageTitle);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const trackEvent = (eventName: string, parameters?: any) =>
  analytics.trackEvent(eventName, parameters);

export const trackAgentEvent = (
  action: string,
  agentId?: string,
  agentName?: string,
) => analytics.trackAgentEvent(action, agentId, agentName);

export const trackSearch = (searchTerm: string, resultsCount?: number) =>
  analytics.trackSearch(searchTerm, resultsCount);

export const trackError = (
  errorType: string,
  errorMessage?: string,
  errorCode?: string,
) => analytics.trackError(errorType, errorMessage, errorCode);
